import Link from "next/link";
import { Mail, Phone, MapPin } from "lucide-react";
import Image from "next/image";
import {
  SITE_PHONE_E164,
  SITE_PHONE_DISPLAY,
  SITE_SECONDARY_PHONE_E164,
  SITE_SECONDARY_PHONE_DISPLAY,
  SITE_EMAIL,
  GOOGLE_MAPS_DIRECTIONS_URL,
  INSTAGRAM_URL,
  YOUTUBE_URL,
} from "@/lib/site-config";

function InstagramBrandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8A3.6 3.6 0 0 0 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 4.98 18.02 3 15.4 3H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 7 1.25 1.25 0 0 1 16 5.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"
      />
    </svg>
  );
}

function YouTubeBrandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.95.3-2.51.44-4.83.44H8c-2.19 0-3.8-.16-4.83-.44-.9-.25-1.48-.83-1.73-1.73C.96 15.8.8 14.19.8 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73C3.2 4.96 4.81 4.8 7 4.8h5.19c2.32 0 3.88.14 4.83.44.9.25 1.48.83 1.73 1.73.28 1.03.44 2.64.44 4.83z"
      />
    </svg>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border bg-background pt-12 sm:pt-16 pb-6 sm:pb-8">
      <div className="site-container">
        <div className="mb-12 grid grid-cols-1 gap-12 md:grid-cols-4">
          {/* Column 1: Logo & Socials */}
          <div className="md:col-span-1 flex flex-col items-start">
            <Link href="/" className="group mb-6 flex items-center gap-2">
              <Image
                src="/logo_dark.png"
                alt="Mont2 Academy Logo"
                width={120}
                height={120}
                className="h-16 w-auto object-contain transition-all duration-300 dark:hidden sm:h-20"
              />
              <Image
                src="/logo.png"
                alt="Mont2 Academy Logo"
                width={120}
                height={120}
                className="hidden h-16 w-auto object-contain transition-all duration-300 dark:block sm:h-20"
              />
            </Link>
            <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Follow us</p>
            <div className="flex items-center gap-3">
              <Link
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-border/80 bg-card/60 text-[#E4405F] shadow-sm transition-all hover:scale-105 hover:border-[#E4405F]/50 hover:bg-[#E4405F]/10 hover:shadow-md dark:border-white/10"
                aria-label="Instagram"
              >
                <InstagramBrandIcon className="h-5 w-5" />
              </Link>
              <Link
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-border/80 bg-card/60 text-[#FF0000] shadow-sm transition-all hover:scale-105 hover:border-[#FF0000]/50 hover:bg-[#FF0000]/10 hover:shadow-md dark:border-white/10"
                aria-label="YouTube"
              >
                <YouTubeBrandIcon className="h-5 w-5" />
              </Link>
            </div>
            {/* ISO certification reference */}
            <div className="mt-6 flex items-center gap-3 text-xs font-semibold text-muted-foreground border-t border-border/40 pt-4 w-full">
              <div className="relative w-8 h-10 overflow-hidden shrink-0 border border-border/80 rounded bg-white">
                <Image
                  src="/iso_certified.jpeg"
                  alt="ISO 9001:2015"
                  fill
                  className="object-cover"
                  sizes="32px"
                />
              </div>
              <div className="leading-tight">
                <p className="text-[10px] text-foreground font-bold">ISO 9001:2015</p>
                <p className="text-[9px] text-muted-foreground">Certified Academy</p>
              </div>
            </div>
          </div>

          {/* Column 2: Programs */}
          <div className="md:col-span-1 pt-2">
            <h4 className="mb-6 font-bold font-heading text-foreground uppercase tracking-wider text-sm">Programs</h4>
            <ul className="space-y-4 text-sm text-muted-foreground font-medium">
              <li>
                <Link href="/courses" className="transition-colors hover:text-mont-violet">
                  All courses (INR fees)
                </Link>
              </li>
              <li>
                <Link href="/courses/360-data-science" className="transition-colors hover:text-mont-violet">
                  360 Data Science
                </Link>
              </li>
              <li>
                <Link href="/courses/data-analysts" className="transition-colors hover:text-mont-violet">
                  Data Analyst
                </Link>
              </li>
              <li>
                <Link href="/courses/python-ml" className="transition-colors hover:text-mont-violet">
                  Python &amp; Machine Learning
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Quick Links */}
          <div className="md:col-span-1 pt-2">
            <h4 className="mb-6 font-bold font-heading text-foreground uppercase tracking-wider text-sm">Quick Links</h4>
            <ul className="space-y-4 text-sm text-muted-foreground font-medium">
              <li>
                <Link href="/upcoming-batch" className="transition-colors hover:text-mont-violet">
                  Upcoming batch
                </Link>
              </li>
              <li>
                <Link href="/tools" className="transition-colors hover:text-mont-violet">
                  Tools we teach
                </Link>
              </li>
              <li>
                <Link href="/faq" className="transition-colors hover:text-mont-violet">
                  FAQ
                </Link>
              </li>
              <li>
                <Link href="/reviews" className="transition-colors hover:text-mont-violet">
                  Reviews &amp; maps
                </Link>
              </li>
              <li>
                <Link href="/about" className="transition-colors hover:text-mont-violet">
                  About us
                </Link>
              </li>
              <li>
                <Link href="/careers" className="transition-colors hover:text-mont-violet">
                  Careers
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact Info */}
          <div className="md:col-span-1 pt-2">
            <h4 className="mb-6 font-bold font-heading text-foreground uppercase tracking-wider text-sm">Location & Contact</h4>
            <div className="space-y-4 text-sm text-muted-foreground font-medium">
              <Link
                href={GOOGLE_MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-3 transition-colors hover:text-mont-violet"
              >
                <MapPin size={18} className="mt-0.5 shrink-0 text-mont-orange transition-transform group-hover:scale-110" />
                <span className="leading-snug">
                  First Floor, LIC Building, Near Mehrauli Bus Stand, South Delhi, New Delhi - 110030
                </span>
              </Link>
              <Link
                href={`tel:${SITE_PHONE_E164}`}
                className="group flex items-center gap-3 transition-colors hover:text-mont-violet"
              >
                <Phone size={18} className="shrink-0 text-mont-orange transition-transform group-hover:scale-110" />
                <span>{SITE_PHONE_DISPLAY}</span>
              </Link>
              <Link
                href={`tel:${SITE_SECONDARY_PHONE_E164}`}
                className="group flex items-center gap-3 transition-colors hover:text-mont-violet"
              >
                <Phone size={18} className="shrink-0 text-mont-orange transition-transform group-hover:scale-110" />
                <span>{SITE_SECONDARY_PHONE_DISPLAY}</span>
              </Link>
              <Link
                href={`mailto:${SITE_EMAIL}`}
                className="group flex items-center gap-3 transition-colors hover:text-mont-violet"
              >
                <Mail size={18} className="shrink-0 text-mont-orange transition-transform group-hover:scale-110" />
                <span>{SITE_EMAIL}</span>
              </Link>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-border pt-8 text-xs text-muted-foreground md:flex-row">
          <p>© {new Date().getFullYear()} MONT2 Academy. All rights reserved.</p>
          <div className="flex gap-4">
            <Link href="/faq" className="transition-colors hover:text-foreground">
              FAQ
            </Link>
            <Link href="/contact" className="transition-colors hover:text-foreground">
              Contact
            </Link>
            <Link href="/reviews" className="transition-colors hover:text-foreground">
              Reviews
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
